/**
 * SA Tools - IPA Content Section
 * Edit and AI-enhance report content sections
 */
import React, { useState } from 'react';
import { Paper, Title, Text, Textarea, Stack, Button, Group, Alert, LoadingOverlay } from '@mantine/core';
import { IconSparkles } from '@tabler/icons-react';
import aiService from '../../services/aiService';

function IPAContentSection({ context, setContext, customerId }) {
  const [rewritingIntro, setRewritingIntro] = useState(false);
  const [rewritingApproach, setRewritingApproach] = useState(false);
  const [rewritingRecommended, setRewritingRecommended] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleRewriteIntroduction = async () => {
    if (!customerId || !context.customer) {
      setError('Please save customer context first');
      return;
    }

    setRewritingIntro(true);
    setError(null);
    setSuccess(null);

    try {
      // Pre-substitute placeholder tokens before sending to AI so the model
      // sees actual values in the text, not generic placeholders
      const prefilledIntro = (context.introduction || '')
        .replace(/\bCUSTOMER\b/g, context.customer || 'CUSTOMER')
        .replace(/\bDATE\b/g, context.ipa_date || 'DATE')
        .replace(/LINES OF BUSINESSS? - LOB|LINES OF BUSINESSS?/g, context.lob || 'LOB');
      const contextWithPrefilled = { ...context, introduction: prefilledIntro };
      const result = await aiService.rewriteIntroduction(customerId, contextWithPrefilled);

      const updatedContext = { ...context, introduction: result.introduction };
      setContext(updatedContext);

      // Save to sessionStorage
      sessionStorage.setItem(`ipa-context-${customerId}`, JSON.stringify(updatedContext));

      setSuccess('Introduction rewritten successfully with AI!');
    } catch (err) {
      console.error('Error rewriting introduction:', err);
      setError(`Failed to rewrite introduction: ${err.message}`);
    } finally {
      setRewritingIntro(false);
    }
  };

  const handleRewriteApproach = async () => {
    if (!customerId || !context.customer) {
      setError('Please save customer context first');
      return;
    }

    setRewritingApproach(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await aiService.rewriteApproach(customerId, context);

      const updatedContext = { ...context, approach: result.approach };
      setContext(updatedContext);

      sessionStorage.setItem(`ipa-context-${customerId}`, JSON.stringify(updatedContext));

      setSuccess('Approach rewritten successfully with AI!');
    } catch (err) {
      console.error('Error rewriting approach:', err);
      setError(`Failed to rewrite approach: ${err.message}`);
    } finally {
      setRewritingApproach(false);
    }
  };

  const handleRewriteRecommendedApproach = async () => {
    if (!customerId || !context.customer) {
      setError('Please save customer context first');
      return;
    }

    setRewritingRecommended(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await aiService.rewriteRecommendedApproach(customerId, context);

      const updatedContext = { ...context, recommended_approach: result.recommended_approach };
      setContext(updatedContext);

      sessionStorage.setItem(`ipa-context-${customerId}`, JSON.stringify(updatedContext));

      setSuccess('Recommended approach rewritten successfully with AI!');
    } catch (err) {
      console.error('Error rewriting recommended approach:', err);
      setError(`Failed to rewrite recommended approach: ${err.message}`);
    } finally {
      setRewritingRecommended(false);
    }
  };

  const handleChange = (field, value) => {
    setContext({ ...context, [field]: value });
  };

  return (
    <Paper shadow="sm" p="xl" radius="md" pos="relative">
      <LoadingOverlay visible={rewritingIntro || rewritingApproach || rewritingRecommended} />

      <Stack gap="lg">
        <div>
          <Title order={2} c="purple">IPA Content</Title>
          <Text c="dimmed" size="sm" mt="xs">
            Customize the introduction, approach, and recommendations for your report. Use AI to enhance any section with customer-specific content.
          </Text>
        </div>

        {error && (
          <Alert color="red" title="Error" onClose={() => setError(null)} withCloseButton>
            {error}
          </Alert>
        )}

        {success && (
          <Alert color="green" title="Success" onClose={() => setSuccess(null)} withCloseButton>
            {success}
          </Alert>
        )}

        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="sm" fw={500}>Introduction</Text>
            <Button
              size="xs"
              variant="light"
              color="purple"
              leftSection={<IconSparkles size={14} />}
              onClick={handleRewriteIntroduction}
              disabled={rewritingIntro || !context.customer}
              loading={rewritingIntro}
            >
              {rewritingIntro ? 'Rewriting...' : 'Rewrite with AI'}
            </Button>
          </Group>
          <Textarea
            placeholder="Enter introduction text..."
            minRows={10}
            value={context.introduction || ''}
            onChange={(e) => handleChange('introduction', e.target.value)}
            resize="vertical"
          />
          {!context.customer && (
            <Text size="xs" c="dimmed">
              Save customer context first to enable AI rewrite
            </Text>
          )}
        </Stack>

        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="sm" fw={500}>Approach</Text>
            <Button
              size="xs"
              variant="light"
              color="purple"
              leftSection={<IconSparkles size={14} />}
              onClick={handleRewriteApproach}
              disabled={rewritingApproach || !context.customer}
              loading={rewritingApproach}
            >
              {rewritingApproach ? 'Rewriting...' : 'Rewrite with AI'}
            </Button>
          </Group>
          <Textarea
            placeholder="Enter approach description..."
            minRows={8}
            value={context.approach || ''}
            onChange={(e) => handleChange('approach', e.target.value)}
            resize="vertical"
          />
        </Stack>

        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="sm" fw={500}>Recommended Approach</Text>
            <Button
              size="xs"
              variant="light"
              color="purple"
              leftSection={<IconSparkles size={14} />}
              onClick={handleRewriteRecommendedApproach}
              disabled={rewritingRecommended || !context.customer}
              loading={rewritingRecommended}
            >
              {rewritingRecommended ? 'Rewriting...' : 'Rewrite with AI'}
            </Button>
          </Group>
          <Textarea
            placeholder="Enter recommended approach..."
            minRows={8}
            value={context.recommended_approach || ''}
            onChange={(e) => handleChange('recommended_approach', e.target.value)}
            resize="vertical"
          />
        </Stack>

        <Text size="xs" c="dimmed" ta="center">
          Changes are automatically saved
        </Text>

        {!context.customer && (
          <Text size="sm" c="dimmed" ta="center">
            Add customer information in the "Customer Context" tab first
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

export default IPAContentSection;
